import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllNotice,
  getSingleNotice,
  createNotice,
  updateNotice,
  deleteNotice,
  toggleNotice,
} from "@/api/online-ordering/settings/notice";

// Thunks

export const fetchAllNotice = createAsyncThunk(
  "notices/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllNotice(restaurant_id);
    if (res.status === "success") return res.data.notices;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleNotice = createAsyncThunk(
  "notices/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleNotice(id);
    if (res.status === "success") return res.data.notice;
    return rejectWithValue(res.message);
  }
);

export const createNewNotice = createAsyncThunk(
  "notices/create",
  async (data, { rejectWithValue }) => {
    const res = await createNotice(data);
    if (res.status === "success") return res.data.notice;
    return rejectWithValue(res.message);
  }
);

export const updateExistingNotice = createAsyncThunk(
  "notices/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateNotice(id, data);
    if (res.status === "success") return res.data.notice;
    return rejectWithValue(res.message);
  }
);

export const updateToggleNotice = createAsyncThunk("notices/toggle", async ({ id }, { rejectWithValue }) => {
    const res = await toggleNotice(id);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

export const deleteNoticeById = createAsyncThunk(
  "notices/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteNotice(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  notices: [],
  singleNotice: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const noticeSlice = createSlice({
  name: "notices",
  initialState,
  reducers: {
    clearNoticeMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchAllNotice.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllNotice.fulfilled, (state, action) => {
        state.getLoading = false;
        state.notices = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllNotice.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleNotice.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleNotice.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleNotice = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleNotice.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewNotice.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewNotice.fulfilled, (state, action) => {
        state.createLoading = false;
        state.notices.push(action.payload);
        state.success = "Notice created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewNotice.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingNotice.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingNotice.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.notices = state.notices.map((notice) =>
          notice.id === action.payload.id ? action.payload : notice
        );
        state.success = "Notice updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingNotice.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateToggleNotice.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateToggleNotice.fulfilled, (state, action) => {
          state.updateLoading = false;          
          state.notices = state.notices.map((notice) =>
            notice.id === action.payload.notice.id
              ? {
                  ...notice,
                  status: action.payload.notice.status,
                }
              : notice
          );

          state.success = "Updated successfully!";
          state.requestStatus = true;
      })
      .addCase(updateToggleNotice.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })

      // Delete
      .addCase(deleteNoticeById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteNoticeById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.notices = state.notices.filter(
          (notice) => notice.id !== action.payload
        );
        state.success = "Notice deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteNoticeById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearNoticeMessages } = noticeSlice.actions;
export default noticeSlice.reducer;
